<script lang="ts">
/**
 * One pinned frame, five sectors in turn: each led by its name in large type, one very specific
 * job, the pain, the outcome Pre6 delivers and how the agents get there, told mostly in drawings on
 * a drafting sheet.
 * - Aerospace: a Ti-6Al-4V pylon bracket whose every cost line traces to a source, AS9102 FAI ready.
 * - Defense: a spares RFQ for a turret drive housing last made in 2008, recalled from memory.
 * - Automotive: an EV drive-unit program of 24 die-cast parts, 3 weeks against 24 hours.
 * - Heavy industries: a 4.2 m wind-turbine yaw flange drawing checked against its 3D model.
 * - Research labs: a CubeSat side panel, each revision priced from the last, estimates tightening.
 *
 * The scroll picks the sector (the wrapper is one band of scroll per sector); a clock plays its
 * sheet. Each time a sector comes up, its sheet plays through once in PLAY seconds and holds its
 * last frame until the scroll moves on to another sector. The rail scrolls to a sector's band. When
 * the frame leaves the screen entirely it goes back to the first frame, so it plays again every time
 * someone comes back to it. Elements with `data-s` (step) and
 * `data-u` (point within the step) switch on as the clock passes them and stay on; CSS transitions
 * do the motion, with overshoot on arrivals. The sheets are ink drawings that plot themselves line by line
 * as they open, and whatever an agent adds is drawn in the hero's style, in that agent's color. JS
 * writes the two continuous sweeps (memory search and drawing scan) and `--u` for the sector rail.
 */
import Calculator from "lucide-svelte/icons/calculator";
import Car from "lucide-svelte/icons/car";
import Check from "lucide-svelte/icons/check";
import Cog from "lucide-svelte/icons/cog";
import Crosshair from "lucide-svelte/icons/crosshair";
import Factory from "lucide-svelte/icons/factory";
import Library from "lucide-svelte/icons/library";
import FlaskConical from "lucide-svelte/icons/flask-conical";
import Network from "lucide-svelte/icons/network";
import Plane from "lucide-svelte/icons/plane";
import Shield from "lucide-svelte/icons/shield";
import { onMount } from "svelte";
import Highlight from "$lib/components/home/highlight.svelte";
import SectionHeading from "$lib/components/home/section-heading.svelte";

// The agents keep the hero's colors (page variables set in enterprise-page.svelte), so the same agent
// looks the same everywhere on the page.
const RFQ = { name: "RFQ", color: "var(--agent-rfq)", icon: Calculator };
const PRE6 = { name: "Pre6", color: "var(--agent-pre6)", icon: Network };
const PROD = { name: "Production", color: "var(--agent-prod)", icon: Cog };
const QA = { name: "Quality", color: "var(--agent-qa)", icon: Crosshair };

/**
 * `pain` is the problem the sector lives with, `title` the outcome, `how` the agents' part in it.
 * `niche` is the one concrete job the sheet walks through. `crew` and `work` label the sheet while the
 * agents are on it; `part` names what is on the sheet.
 */
const SECTORS = [
	{
		id: "aerospace",
		name: "Aerospace",
		icon: Plane,
		niche: "A Ti-6Al-4V pylon bracket, shipped with its AS9102 first article",
		pain: "Customers and auditors ask where every figure in a quote came from.",
		title: "Defend every cost estimate",
		how: "The RFQ Agent breaks each estimate down by operation.",
		agents: [RFQ],
		crew: "RFQ Agent",
		work: "Breaking down by operation",
		part: "Pylon bracket · Ti-6Al-4V",
	},
	{
		id: "defense",
		name: "Defense",
		icon: Shield,
		niche: "A spares order for a turret drive housing last built in 2008",
		pain: "Spares orders arrive for parts last made years ago, and the people who quoted them have since retired.",
		title: "Keep your know-how working",
		how: "The Pre6 Agent finds the old job in memory and drafts the quote from it.",
		agents: [PRE6],
		crew: "Pre6 Agent",
		work: "Searching 22 years of jobs",
		part: "Turret drive housing · rev C",
	},
	{
		id: "automotive",
		name: "Automotive",
		icon: Car,
		niche: "An EV drive-unit program with 24 die-cast aluminum parts",
		pain: "A new vehicle program brings dozens of parts and 21 days to quote them.",
		title: "Let your experts make the call",
		how: "The agents draft the quote, operations plan and inspection plan, and you approve them.",
		agents: [RFQ, PROD, QA],
		crew: "Three agents",
		work: "Drafting 24 parts",
		part: "EV drive unit · 24 HPDC parts",
	},
	{
		id: "heavy",
		name: "Heavy industries",
		icon: Factory,
		niche: "A 4.2 m yaw-bearing flange for a wind turbine",
		pain: "On a flange this size, one missing tolerance can scrap tons of steel.",
		title: "Catch drawing issues early",
		how: "The Quality Agent runs GD&T and DFM checks on every drawing.",
		agents: [QA],
		crew: "Quality Agent",
		work: "Checking against the 3D model",
		part: "Yaw-bearing flange · Ø4200",
	},
	{
		id: "labs",
		name: "Research labs",
		icon: FlaskConical,
		niche: "A CubeSat side panel, revised every week as the payload settles",
		pain: "Prototype designs change every week, and each revision gets quoted from scratch.",
		title: "Price every revision from the last",
		how: "The RFQ Agent compares each revision with the one before it.",
		agents: [RFQ],
		crew: "RFQ Agent",
		work: "Re-costing what changed",
		part: "CubeSat side panel · Al 7075",
	},
];

/** Every scene is drawn in a 520 × 330 box; labels are HTML placed in the same coordinates. */
const at = (x: number, y: number) => `left:${(x / 5.2).toFixed(2)}%;top:${(y / 3.3).toFixed(2)}%`;
const wide = (w: number) => `width:${(w / 5.2).toFixed(2)}%`;

/*
 * Line work, drafting-room style: every shape is a path, so it can draw itself like a pen plotter
 * (pathLength works on paths in every browser).
 */
const circ = (cx: number, cy: number, r: number) => `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`;
const ell = (cx: number, cy: number, rx: number, ry: number) =>
	`M${cx - rx} ${cy}a${rx} ${ry} 0 1 0 ${2 * rx} 0a${rx} ${ry} 0 1 0 ${-2 * rx} 0`;
const rrect = (x: number, y: number, w: number, h: number, r: number) =>
	`M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`;

// Aerospace: the pylon bracket. Each feature the RFQ Agent costs is traced in its color and leads to
// the cost line it drives. The lines sum to ₹18,400.
const BRACKET = "M30 266H232V236H112Q98 236 98 222V78Q98 58 78 58H50Q30 58 30 78Z";
const AERO = [
	{ label: "Ti-6Al-4V bar", value: "₹6,200", src: "AMS 4928 · 3.1 kg", feat: "M24 52H238V272H24Z", stock: true, ax: 238, ay: 70, y: 70, lead: "M238 70H256", u: 0.1 },
	{ label: "5-axis pockets", value: "₹9,800", src: "42 min ← Q-0412, 2022", feat: rrect(42, 108, 44, 52, 7) + rrect(42, 170, 44, 48, 7), stock: false, ax: 86, ay: 134, y: 135, lead: "M86 134H256", u: 0.28 },
	{ label: "FPI on the fillet", value: "₹1,000", src: "ASTM E1417 ← FAI, 2023", feat: "M112 236Q98 236 98 222", stock: false, ax: 102, ay: 232, y: 200, lead: "M102 232L238 200H256", u: 0.46 },
	{ label: "Ream 3 holes", value: "₹1,400", src: "← DWG-118 routing", feat: circ(140, 251, 6) + circ(172, 251, 6) + circ(204, 251, 6), stock: false, ax: 210, ay: 251, y: 265, lead: "M210 251L238 265H256", u: 0.64 },
];

// Defense: 22 years of drawings in memory, as a strip of sheets. The 2008 sheet sits at x 120 on the
// bottom row and is the one that lifts out.
const yearX = (y: number) => 40 + ((y - 2004) / 22) * 440;
const YEARS = [2004, 2008, 2016, 2024];
const JOBS = Array.from({ length: 19 }, (_, i) => ({ x: yearX(2004.6 + (i * 21) / 19) })).filter((j) => Math.abs(j.x - 120) > 10);
const sheetIcon = (x: number, y: number) => `M${x - 4.5} ${y}h9v12h-9ZM${x - 4.5} ${y + 9}h9`;

// Automotive: the program's bill of materials, costed row by row (four parts a row, 24 in all),
// then the same RFQ on two 21-day calendars.
const BOM = [
	["Motor housing", "₹2,840"],
	["Gearbox cover", "₹1,120"],
	["Inverter tray", "₹1,960"],
	["Bearing cap", "₹310"],
	["Oil pan", "₹780"],
	["Cooling jacket", "₹1,540"],
].map(([name, cost], i) => ({ n: i + 1, name, cost, u: 0.1 + i * 0.035 }));
const DAYS = Array.from({ length: 21 }, (_, d) => ({ x: 300 + d * 9.8, u: 0.38 + d * 0.024 }));

// Heavy industries: the yaw flange, its 16 bolt holes, and the three issues the scan flags, each
// matched on the model.
const BOLTS = Array.from({ length: 16 }, (_, k) => (Math.PI / 180) * (11.25 + k * 22.5));
const FLAGS = [
	{ n: 1, text: "Bolt hole ≠ model", u: 0.22 },
	{ n: 2, text: "Ø4200 lacks tolerance", u: 0.38 },
	{ n: 3, text: "Datum B missing", u: 0.63 },
];
/**
 * A hole X-rayed through the flange on the model: its rim as the model's own ellipse, and its
 * cylinder down through the 28-unit thickness, hidden edges dashed.
 */
const xray = (cx: number, cy: number, rx: number, ry: number, depth = 28) => ({
	fill: `M${cx - rx} ${cy}V${cy + depth}A${rx} ${ry} 0 0 0 ${cx + rx} ${cy + depth}V${cy}A${rx} ${ry} 0 0 1 ${cx - rx} ${cy}Z`,
	hidden: `M${cx - rx} ${cy}V${cy + depth}M${cx + rx} ${cy}V${cy + depth}${ell(cx, cy + depth, rx, ry)}`,
	rim: ell(cx, cy, rx, ry),
});
const BOLT_XRAY = xray(339.2, 107.3, 4.4, 1.9);
const BORE_XRAY = xray(400, 112, 32, 12.4);

// Research labs: three revisions of the panel, each change drawn in the agent's color; the
// estimate's spread narrows with each build.
const REVS = [
	{ name: "Rev A", x: 20, delta: "₹4,200", note: "baseline", est: 262, spread: 40, pct: "±18%", u: 0.05 },
	{ name: "Rev B", x: 190, delta: "+₹320", note: "harness slot", est: 249, spread: 20, pct: "±9%", u: 0.22 },
	{ name: "Rev C", x: 360, delta: "+₹180", note: "4 hinge holes", est: 257, spread: 8, pct: "±4%", u: 0.4 },
];
/** The panel's four corner rails, 7 units square, inside an 80 × 80 panel on a 140-wide card. */
const RAILS = (x: number) => [[x + 30, 44], [x + 103, 44], [x + 30, 117], [x + 103, 117]].map(([rx, ry]) => `M${rx} ${ry}h7v7h-7Z`).join("");

/**
 * Staging: each sheet walks through beats, one thing in focus at a time while the rest steps back.
 * Each list holds the `u` at which each beat starts; the last value is where every beat comes back
 * together as the summary. Elements join a beat with `data-k` (a space-separated list of beat numbers).
 */
const BEATS = [
	[0.1, 0.28, 0.46, 0.64, 0.82], // aerospace: bar stock, pockets, fillet, holes, then the total
	[0.08, 0.5, 0.64, 0.9], // defense: the search, the 2008 drawing, the drafted RFQ
	[0.08, 0.36, 0.9], // automotive: the BOM, then the calendars
	[0.22, 0.38, 0.63, 0.86], // heavy industries: issue 1, 2 and 3, then all three
	[0.05, 0.22, 0.4, 0.58, 0.9], // research labs: rev A, B and C, then the estimate
];

/** Seconds each sector's sheet takes to play from its first frame to its last. */
const PLAY = 4;
/** Seconds between a sector coming up and its sheet's clock starting: the copy cascades in first. */
const LEAD_IN = 0.4;
/** How much of the frame must be on screen before it starts to play. */
const PLAY_AT = 0.45;

let root: HTMLDivElement;
let active = $state(0);
/** The frame has come up: its sheet is playing, or finished and holding. */
let live = $state(false);
/** The agents on the current sheet have finished and handed it over for review. */
let ready = $state(false);

/** Scrolls to the middle of a sector's band; its sheet plays as it comes up. */
function goTo(i: number) {
	const r = root.getBoundingClientRect();
	const run = Math.max(1, r.height - innerHeight);
	window.scrollTo({ top: r.top + scrollY + ((i + 0.5) / SECTORS.length) * run, behavior: "instant" });
}

onMount(() => {
	const frame = root.querySelector<HTMLElement>(".frame");
	const timed = Array.from(root.querySelectorAll<HTMLElement>("[data-s]"));
	const scenes = Array.from(root.querySelectorAll<HTMLElement>("[data-scene]"));
	const beam = root.querySelector<SVGRectElement>(".beam");
	const scan = root.querySelector<SVGGElement>(".scan");
	const bomCount = root.querySelector<HTMLElement>(".bom-n");
	const ease = (t: number) => t * t * (3 - 2 * t);
	const span = (u: number, from: number, to: number) => ease(Math.min(1, Math.max(0, (u - from) / (to - from))));
	const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
	const n = SECTORS.length;

	function render(step: number, u: number) {
		active = step;
		for (const el of timed) {
			const s = Number(el.dataset.s);
			el.toggleAttribute("data-on", step > s || (step === s && u >= Number(el.dataset.u ?? 0)));
		}
		for (const el of scenes) el.toggleAttribute("data-active", live && Number(el.dataset.scene) === step);
		root.style.setProperty("--u", u.toFixed(3));
		ready = u >= 0.88;

		// Defense: the search sweeps back through the years and stops on 2008.
		const back = step > 1 ? 1 : step < 1 ? 0 : span(u, 0.08, 0.45);
		beam?.setAttribute("x", (466 - back * 360).toFixed(1));
		beam?.style.setProperty("opacity", step === 1 && u > 0.04 && u < 0.56 ? "1" : "0");

		// Automotive: four parts are costed with each BOM row.
		const rows = step > 2 ? BOM.length : step < 2 ? 0 : BOM.filter((b) => u >= b.u).length;
		if (bomCount) bomCount.textContent = String(rows * 4);

		// Staging: the active sheet's current beat, or none before the first and after the summary.
		for (const el of scenes) {
			const k = Number(el.dataset.scene);
			const starts = BEATS[k];
			const m = k === step ? starts.filter((x) => u >= x).length : 0;
			const beat = m > 0 && m < starts.length ? String(m - 1) : "";
			if ((el.dataset.beat ?? "") !== beat) {
				if (beat) el.dataset.beat = beat;
				else delete el.dataset.beat;
			}
		}

		// Heavy industries: the scan line crosses the drawing.
		const across = step > 3 ? 1 : step < 3 ? 0 : span(u, 0.06, 0.78);
		scan?.setAttribute("transform", `translate(${(20 + across * 250).toFixed(1)} 0)`);
		scan?.style.setProperty("opacity", step === 3 && u > 0.03 && u < 0.82 ? "1" : "0");
	}

	/** Which sector the scroll is on: one band per sector through the tall wrapper. */
	function scrolled() {
		const r = root.getBoundingClientRect();
		const p = Math.min(0.9999, Math.max(0, -r.top / Math.max(1, r.height - innerHeight)));
		return Math.floor(p * n);
	}

	// The clock plays one sector's sheet, then stops and holds it.
	let step = 0;
	let start = 0;
	let raf = 0;

	function tick(now: number) {
		const u = Math.min(1, Math.max(0, (now - start) / (PLAY * 1000)));
		render(step, u);
		raf = u < 1 ? requestAnimationFrame(tick) : 0;
	}

	/** Plays sector `i`'s sheet from its first frame. */
	function play(i: number) {
		cancelAnimationFrame(raf);
		raf = 0;
		step = i;
		live = true;
		if (still) return render(i, 1);
		start = performance.now() + LEAD_IN * 1000;
		render(i, 0);
		raf = requestAnimationFrame(tick);
	}

	/** Back to the first frame, ready to play again. */
	function reset() {
		cancelAnimationFrame(raf);
		raf = 0;
		live = false;
		step = scrolled();
		render(step, 0);
	}

	function onScroll() {
		const i = scrolled();
		if (live && i !== step) play(i);
		else if (!live) {
			step = i;
			render(i, 0);
		}
	}

	reset();
	const io = new IntersectionObserver(
		([e]) => {
			if (e.intersectionRatio >= PLAY_AT) {
				if (!live) play(scrolled());
			} else if (!e.isIntersecting) reset();
		},
		{ threshold: [0, PLAY_AT] },
	);
	if (frame) io.observe(frame);
	addEventListener("scroll", onScroll, { passive: true });
	return () => {
		io.disconnect();
		cancelAnimationFrame(raf);
		removeEventListener("scroll", onScroll);
	};
});
</script>

<div class="sectors" bind:this={root} style:--steps={SECTORS.length}>
	<div class="frame" data-drafting="sectors">
		<ol class="rail">
			{#each SECTORS as s, i (s.id)}
				{@const Icon = s.icon}
				<li data-active={i === active || undefined} data-done={i < active || undefined}>
					<button type="button" aria-label="Go to {s.name}" aria-current={i === active ? "step" : undefined} onclick={() => goTo(i)}>
						<span class="r-icon"><Icon size={15} strokeWidth={2} /></span>
						<span class="r-name">{s.name}</span>
					</button>
					<span class="r-fill"></span>
				</li>
			{/each}
		</ol>

		<!-- The sector, set large across the top, with the one concrete job its sheet walks through. -->
		<div class="reg-name">
			{#each SECTORS as s, i (s.id)}
				<div class="slide" data-active={(live && i === active) || undefined} aria-hidden={i !== active}>
					<p class="sector"><span class="s-no">0{i + 1}</span><span class="s-mask"><span class="s-name">{s.name}</span></span></p>
					<p class="niche"><span>For example</span>{s.niche}</p>
				</div>
			{/each}
		</div>

		<!-- Left margin: the pain, leading into the sheet. -->
		<div class="reg-pain">
			{#each SECTORS as s, i (s.id)}
				<div class="slide" data-active={(live && i === active) || undefined} aria-hidden={i !== active}>
					<p class="pain"><span>The pain</span>{s.pain}</p>
				</div>
			{/each}
		</div>

		<div class="stage-wrap" aria-hidden="true">
		<div class="stage" data-ready={ready || undefined}>
			<div class="bar">
				<span class="crew">
					{#each SECTORS as s, i (s.id)}
						<span class="crew-row" data-active={i === active || undefined}>
							<span class="avs">
								{#each s.agents as a (a.name)}<span class="av" style:--c={a.color}><a.icon size={12} strokeWidth={2.25} /></span>{/each}
							</span>
							<span class="crew-text">
								<b>{s.crew}</b>
								<span class="state" data-ready={(i === active && ready) || undefined}>
									<span class="working"><span class="dots"><i></i><i></i><i></i></span>{s.work}</span>
									<span class="ready"><Check size={11} strokeWidth={3} />Ready for your review</span>
								</span>
							</span>
						</span>
					{/each}
				</span>
				<span class="sheet-no">0{active + 1}<small>/0{SECTORS.length}</small></span>
			</div>

			<div class="view">
				<div class="box">
					<!-- Shared drafting symbols: dimension arrowheads and section hatching. -->
					<svg class="defs" width="0" height="0" aria-hidden="true">
						<defs>
							<marker id="ss-arr" viewBox="0 0 8 8" refX="7.5" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
								<path d="M0 1.2L8 4L0 6.8Z" />
							</marker>
							<pattern id="ss-hatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
								<path d="M0 0V5" />
							</pattern>
						</defs>
					</svg>

					<!-- Aerospace: one costed feature in focus at a time, each led to its cost line; then the total. -->
					<div class="scene" data-scene="0" style:--ag={RFQ.color}>
						<svg viewBox="0 0 520 330">
							<g class="part">
								<path class="ink" pathLength="1" d={BRACKET} />
								<path class="ink" pathLength="1" style:--d="0.3" d={circ(64, 84, 11)} />
								<path class="ink" pathLength="1" style:--d="0.4" d={rrect(42, 108, 44, 52, 7)} />
								<path class="ink" pathLength="1" style:--d="0.5" d={rrect(42, 170, 44, 48, 7)} />
								{#each [140, 172, 204] as cx, i (cx)}<path class="ink" pathLength="1" style:--d={0.6 + i * 0.08} d={circ(cx, 251, 6)} />{/each}
							</g>
							<path class="ctr" d="M64 66V102M46 84H82M64 104V226M128 251H216" />
							<g class="dim">
								<path d="M30 270V298M232 270V298" />
								<path class="arr" d="M30 292H232" />
							</g>
							{#each AERO as c, i (c.label)}
								<g data-k={i}>
									<path class="feat" class:stock={c.stock} d={c.feat} data-s="0" data-u={c.u} />
									<path class="lead" pathLength="1" d={c.lead} data-s="0" data-u={c.u} />
									<circle class="anchor" cx={c.ax} cy={c.ay} r="4" data-s="0" data-u={c.u} />
								</g>
							{/each}
						</svg>
						<span class="lab dimtxt" style={at(131, 292)}>202</span>
						{#each AERO as c, i (c.label)}
							<div class="lab cost" style="{at(262, c.y)};{wide(246)}" data-k={i} data-s="0" data-u={c.u + 0.1}>
								<span class="c-k"><Calculator size={11} strokeWidth={2.25} />{c.label}</span><b>{c.value}</b>
								<span class="c-src">{c.src}</span>
							</div>
						{/each}
						<span class="lab att" style={at(262, 20)} data-s="0" data-u="0.82"><Check size={12} strokeWidth={2.75} /><b>₹18,400 / pc</b><span>4 sources</span></span>
						<span class="lab stamp" style={at(262, 308)} data-s="0" data-u="0.86">AS9102 · FAI ready</span>
					</div>

					<!-- Defense: the search runs back through the years, the 2008 drawing lifts out, the RFQ is drafted from it. -->
					<div class="scene" data-scene="1" style:--ag={PRE6.color}>
						<svg viewBox="0 0 520 330">
							<g data-k="0">
								<path class="thin" d="M40 300H480" />
								{#each YEARS as y (y)}<path class="thin" d="M{yearX(y)} 296V304" />{/each}
								{#each JOBS as j, i (i)}<path class="sheet-ic" d={sheetIcon(j.x, 268)} />{/each}
								<rect class="beam" x="466" y="252" width="28" height="36" rx="4" />
							</g>
							<g data-k="0 1">
								<path class="sheet-ic hit" d={sheetIcon(120, 268)} data-s="1" data-u="0.45" />
								<circle class="ring" cx="120" cy="274" r="11" data-s="1" data-u="0.45" />
							</g>
							<g data-k="1">
								<g class="recall" data-s="1" data-u="0.5">
									<path class="paper" d="M36 28H250V206H36Z" />
									<path class="thin" d="M176 188H250M210 188V206" />
									<path class="ink" pathLength="1" d={rrect(66, 52, 132, 118, 16)} />
									{#each [[80, 66], [184, 66], [80, 156], [184, 156]] as [x, y], i (i)}<path class="ink" pathLength="1" style:--d={0.2 + i * 0.05} d={circ(x, y, 4)} />{/each}
									<path class="ink" pathLength="1" style:--d="0.35" d={circ(132, 111, 30)} />
									<path class="ink" pathLength="1" style:--d="0.45" d={circ(132, 111, 21)} />
									<path class="ctr" d="M92 111H172M132 71V151" />
								</g>
							</g>
							<g data-k="1 2">
								<path class="lead" pathLength="1" d="M250 96H274L290 72H302" data-s="1" data-u="0.6" />
								<circle class="anchor" cx="250" cy="96" r="4" data-s="1" data-u="0.6" />
							</g>
						</svg>
						<span class="lab note" style={at(40, 238)} data-k="0">Memory · 22 years of drawings</span>
						{#each YEARS as y (y)}<span class="lab year" style={at(yearX(y), 315)} data-k="0">{y}</span>{/each}
						<span class="lab tb" style={at(213, 197)} data-k="1" data-s="1" data-u="0.52">DWG-2231 · C</span>
						<div class="lab rfq" style="{at(302, 40)};{wide(208)}" data-k="2">
							<b class="r-t">Spares RFQ</b>
							<span class="att" data-s="1" data-u="0.7"><Library size={11} strokeWidth={2.25} /><b>DWG-2231 · 2008</b></span>
							<span class="att wrap" data-s="1" data-u="0.77"><Library size={11} strokeWidth={2.25} /><b>Note</b><span>MIL-A-8625 Type III</span></span>
							<span class="status"><span>Searching memory…</span><span class="done" data-s="1" data-u="0.84"><Check size={11} strokeWidth={2.75} />Drafted from the 2008 job</span></span>
						</div>
					</div>

					<!-- Automotive: the bill of materials costed row by row, then one RFQ on two calendars. -->
					<div class="scene" data-scene="2" style:--ag={RFQ.color}>
						<svg viewBox="0 0 520 330">
							<g data-k="1">
								<path class="thin" d="M300 151H505M300 247H505" />
								{#each DAYS as d, i (i)}
									<rect class="day" x={d.x} y="128" width="8.6" height="20" rx="1.5" />
									<rect class="day slow" x={d.x} y="128" width="8.6" height="20" rx="1.5" data-s="2" data-u={d.u} />
									<rect class="day" x={d.x} y="224" width="8.6" height="20" rx="1.5" />
									<rect class="day {i < 1 ? 'fast' : 'next'}" x={d.x} y="224" width="8.6" height="20" rx="1.5" data-s="2" data-u={d.u} />
								{/each}
							</g>
						</svg>
						<span class="lab who" style={at(300, 112)} data-k="1">Without Pre6</span>
						<span class="lab who strong" style={at(300, 208)} data-k="1">With Pre6</span>
						<span class="lab sent" style={at(505, 166)} data-k="1" data-s="2" data-u="0.87">Sent in 3 weeks</span>
						<span class="lab sent ok" style={at(505, 262)} data-k="1" data-s="2" data-u="0.42"><Check size={11} strokeWidth={2.75} />Sent in 24 hours</span>
						<div class="lab bom" style="{at(18, 40)};{wide(262)}" data-k="0">
							<div class="bom-h"><span>BOM</span><span>Costed <b class="bom-n">0</b>/24</span></div>
							{#each BOM as b (b.n)}
								<div class="bom-r"><i>{b.n}</i><span>{b.name}</span><b data-s="2" data-u={b.u}>{b.cost}</b></div>
							{/each}
							<div class="bom-more">+18 more parts</div>
						</div>
					</div>

					<!-- Heavy industries: one issue in focus at a time, ringed on the drawing and on the model, then the score. -->
					<div class="scene" data-scene="3" style:--ag={QA.color}>
						<svg viewBox="0 0 520 330">
							<path class="paper" d="M16 20H272V310H16Z" />
							<g class="part">
								<path class="ink" pathLength="1" d={circ(130, 150, 88)} />
								<path class="ink" pathLength="1" style:--d="0.25" d={circ(130, 150, 34)} />
								{#each BOLTS as a, i (i)}<path class="ink" pathLength="1" style:--d={0.35 + i * 0.03} d={circ(130 + 62 * Math.cos(a), 150 + 62 * Math.sin(a), 5)} />{/each}
							</g>
							<path class="ctr" d="{circ(130, 150, 62)}M30 150H230M130 50V250" />
							<g class="dim">
								<path d="M42 150V270M218 150V270" />
								<path class="arr" d="M42 262H218" />
							</g>
							<g class="part">
								<path class="hatch" d="M312 112A88 34 0 0 0 488 112V140A88 34 0 0 1 312 140Z" />
								<path class="ink" pathLength="1" style:--d="0.2" d={ell(400, 112, 88, 34)} />
								<path class="ink" pathLength="1" style:--d="0.4" d="M312 112V140A88 34 0 0 0 488 140V112" />
								<path class="ink" pathLength="1" style:--d="0.55" d={ell(400, 112, 32, 12.4)} />
								{#each BOLTS as a, i (i)}<path class="ink" pathLength="1" style:--d={0.6 + i * 0.02} d={ell(400 + 62 * Math.cos(a), 112 + 24 * Math.sin(a), 4.4, 1.9)} />{/each}
							</g>
							<g class="scan">
								<rect x="-10" y="20" width="20" height="290" />
								<path d="M0 20V310" />
							</g>
							<!-- 1 · A bolt hole: ringed on the drawing, X-rayed through the flange on the model. -->
							<g data-k="0">
								<g class="flag" data-s="3" data-u="0.22">
									<circle class="ring" cx="69.2" cy="137.9" r="11" />
									<circle class="badge" cx="82" cy="125" r="7.5" />
									<text x="82" y="128">1</text>
								</g>
								<path class="lead" pathLength="1" d="M69.2 137.9L94 104H333" data-s="3" data-u="0.25" />
								<g class="flag" data-s="3" data-u="0.28">
									<path class="xr-fill" d={BOLT_XRAY.fill} />
									<path class="xr-hidden" d={BOLT_XRAY.hidden} />
									<path class="xr-rim" d={BOLT_XRAY.rim} />
									<path class="xr-halo" d={ell(339.2, 107.3, 11, 5)} />
								</g>
							</g>
							<!-- 2 · The overall diameter: its dimension boxed on the drawing, the same diameter dimensioned on the model. -->
							<g data-k="1">
								<g class="flag" data-s="3" data-u="0.38">
									<rect class="hl-box" x="108" y="253" width="44" height="18" rx="4" />
									<circle class="badge" cx="156" cy="249" r="7.5" />
									<text x="156" y="252">2</text>
								</g>
								<path class="lead" pathLength="1" d="M152 262H262L312 114" data-s="3" data-u="0.41" />
								<g class="flag" data-s="3" data-u="0.44">
									<path class="rim-hl" d={ell(400, 112, 88, 34)} />
									<path class="dim-ag" d="M312 108V56M488 108V56M318 64H482" />
									<path class="arrow-ag" d="M312 64l7-3.2v6.4ZM488 64l-7-3.2v6.4Z" />
								</g>
							</g>
							<!-- 3 · Datum B on the bore: the bore's edge ringed on the drawing, the bore X-rayed on the model. -->
							<g data-k="2">
								<g class="flag" data-s="3" data-u="0.63">
									<circle class="ring" cx="164" cy="150" r="10" />
									<circle class="badge" cx="176" cy="138" r="7.5" />
									<text x="176" y="141">3</text>
								</g>
								<path class="lead" pathLength="1" d="M164 150L190 176H412L431 118" data-s="3" data-u="0.66" />
								<g class="flag" data-s="3" data-u="0.69">
									<path class="xr-fill" d={BORE_XRAY.fill} />
									<path class="xr-hidden" d={BORE_XRAY.hidden} />
									<path class="xr-rim" d={BORE_XRAY.rim} />
								</g>
							</g>
						</svg>
						<span class="lab note" style={at(26, 34)}>2D drawing</span>
						<span class="lab note" style={at(312, 38)}>3D model</span>
						<span class="lab dimtxt" style={at(130, 262)}>Ø4200</span>
						{#each FLAGS as f, i (f.n)}
							<span class="lab att" style={at(300, 200 + i * 30)} data-k={i} data-s="3" data-u={f.u + 0.1}><Crosshair size={11} strokeWidth={2.25} /><b>{f.n}</b><span>{f.text}</span></span>
						{/each}
					</div>

					<!-- Research labs: one revision in focus at a time, its change drawn in blue; then the estimate tightening. -->
					<div class="scene" data-scene="4" style:--ag={RFQ.color}>
						<svg viewBox="0 0 520 330">
							{#each REVS as r, i (r.name)}
								<g data-k={i}>
									<g data-s="4" data-u={r.u}>
										<path class="paper" d="M{r.x} 24h140v130h-140Z" />
										<path class="ink" pathLength="1" d={rrect(r.x + 30, 44, 80, 80, 3)} />
										<path class="ink" pathLength="1" style:--d="0.2" d={RAILS(r.x)} />
										<path class="ink" pathLength="1" style:--d="0.3" d={circ(r.x + 70, 78, 10)} />
										{#if i >= 1}<path class="ink" class:new={i === 1} pathLength="1" style:--d="0.4" d={rrect(r.x + 52, 100, 36, 7, 3.5)} />{/if}
										{#if i === 2}{#each [51, 63.7, 76.3, 89] as hx (hx)}<path class="ink new" pathLength="1" style:--d="0.45" d={circ(r.x + hx, 57, 2.3)} />{/each}{/if}
									</g>
								</g>
								{#if i > 0}<path class="thin arrow-r" d="M{r.x - 26} 84H{r.x - 5}" data-s="4" data-u={r.u - 0.02} />{/if}
								<g data-k="3">
									<g class="err" data-s="4" data-u={0.6 + i * 0.08}>
										<path d="M{r.x + 70} {r.est - r.spread}V{r.est + r.spread}M{r.x + 64} {r.est - r.spread}h12M{r.x + 64} {r.est + r.spread}h12" />
										<circle cx={r.x + 70} cy={r.est} r="4.5" />
									</g>
								</g>
							{/each}
							<g data-k="3">
								<path class="thin" d="M20 305H500" />
								<path class="actual" d="M20 255H500" data-s="4" data-u="0.58" />
							</g>
						</svg>
						{#each REVS as r, i (r.name)}
							<span class="lab rev" style={at(r.x + 8, 34)} data-k={i} data-s="4" data-u={r.u}>{r.name}</span>
							<span class="lab att" style={at(r.x, 172)} data-k={i} data-s="4" data-u={r.u + 0.04}><b>{r.delta}</b><span>{r.note}</span></span>
							<span class="lab pct" style={at(r.x + 83, r.est)} data-k="3" data-s="4" data-u={0.62 + i * 0.08}>{r.pct}</span>
						{/each}
						<span class="lab note" style={at(20, 214)} data-k="3" data-s="4" data-u="0.58">Estimate vs actual cost</span>
					</div>
				</div>
			</div>

			<div class="tblock">
				<span><small>Sector</small><b>{SECTORS[active].name}</b></span>
				<span><small>Part</small><b>{SECTORS[active].part}</b></span>
				<span><small>Drawn by</small><b>Pre6</b></span>
			</div>
		</div>
		</div>

		<!-- Right margin: what the sheet comes out as, and how the agents got there. -->
		<div class="reg-out">
			{#each SECTORS as s, i (s.id)}
				<div class="slide" data-active={(live && i === active) || undefined} aria-hidden={i !== active}>
					<div class="outcome">
						<p class="out-k">The outcome</p>
						<SectionHeading id="sector-{s.id}" title={s.title}>
							{#if s.id === "aerospace"}
								Every estimate shows the <Highlight>routing, cycle times and past jobs</Highlight> behind it.
							{:else if s.id === "defense"}
								Your experts' judgment stays on file for every part family, so a 2008 part is <Highlight>quoted from its own drawing, routing and notes</Highlight>.
							{:else if s.id === "automotive"}
								Estimators start from a finished draft and spend their time checking it. The quote goes out <Highlight>in hours instead of weeks</Highlight>.
							{:else if s.id === "heavy"}
								Every drawing is checked <Highlight>against its 3D model</Highlight> while the part is still with engineering.
							{:else}
								Only what changed gets re-costed, and estimates <Highlight>tighten with every build</Highlight>.
							{/if}
						</SectionHeading>
					</div>
					<p class="how">
						<span class="avs">
							{#each s.agents as a (a.name)}<span class="av" style:--c={a.color}><a.icon size={12} strokeWidth={2.25} /></span>{/each}
						</span>
						<span><b>How:</b> {s.how}</span>
					</p>
				</div>
			{/each}
		</div>
	</div>
</div>

<style>
	/*
	 * The wrapper is tall; the frame sticks under the navbar for one band of scroll per sector. Copy
	 * and the sector rail on the left, the drawing on the right.
	 */
	.sectors {
		--ease: cubic-bezier(0.2, 0.75, 0.2, 1);
		--pop: cubic-bezier(0.34, 1.56, 0.64, 1);
		--gutter: 32px;
		--line: var(--blue-800);
		position: relative;
		height: calc(100svh + var(--steps) * 75svh);
		color: var(--foreground);
	}

	/*
	 * The frame reads left to right like a story: the sector across the top, the pain in the left
	 * margin leading into the sheet, the sheet in the middle, and what it comes out as in the right
	 * margin. Hairline leaders, like a drawing's, carry the eye through.
	 */
	.frame {
		position: sticky;
		top: var(--nav-h);
		display: grid;
		grid-template-columns: minmax(0, 15rem) minmax(0, 1fr) minmax(0, 19rem);
		grid-template-rows: auto minmax(0, auto);
		grid-template-areas:
			"name name rail"
			"pain stage out";
		align-content: center;
		align-items: center;
		gap: 26px 48px;
		height: calc(100svh - var(--nav-h));
		max-width: var(--page-max);
		margin: 0 auto;
		padding: 24px var(--gutter);
	}

	.reg-name,
	.reg-pain,
	.reg-out {
		position: relative;
		display: grid;
		grid-template-columns: minmax(0, 1fr);
	}

	/* A highlighted phrase wraps with its line here (the shared mark keeps itself on one line), so a
	   long one can't push the narrow outcome column off the screen. */
	.reg-out :global(.mark) {
		white-space: normal;
		-webkit-box-decoration-break: clone;
		box-decoration-break: clone;
	}

	.reg-name {
		grid-area: name;
		align-self: end;
	}

	.reg-pain {
		grid-area: pain;
	}

	.reg-out {
		grid-area: out;
	}

	/* The outcome mirrors the pain: a green label and rule over the heading and its sentence. */
	.outcome {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding-left: 16px;
		border-left: 2px solid var(--ok);
	}

	.out-k {
		color: var(--ok);
		font: 500 11px/16px var(--font-mono);
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}


	.stage-wrap {
		grid-area: stage;
		justify-self: center;
		width: 100%;
		/* As wide as the screen's height allows, so the whole frame fits on one screen. */
		max-width: min(660px, calc((100svh - var(--nav-h) - 330px) * 1.35));
	}

	/* Leaders: a hairline with an arrowhead from the pain into the sheet, and from the sheet out. */
	.reg-pain::before,
	.reg-out::before {
		content: "";
		position: absolute;
		top: 50%;
		width: 34px;
		height: 1px;
		background: color-mix(in oklch, var(--ink) 35%, transparent);
	}

	.reg-pain::after,
	.reg-out::after {
		content: "";
		position: absolute;
		top: calc(50% - 4px);
		border: 4.5px solid transparent;
		border-left: 7px solid color-mix(in oklch, var(--ink) 45%, transparent);
		border-right: 0;
	}

	.reg-pain::before {
		left: calc(100% + 6px);
	}

	.reg-pain::after {
		left: calc(100% + 38px);
	}

	.reg-out::before {
		right: calc(100% + 12px);
	}

	.reg-out::after {
		right: calc(100% + 6px);
	}

	/* The sector rail: a row of sector icons, the current one named, each with a track that fills as you read it. */
	/* The rail sits top right over the name band, always on one line. */
	.rail {
		grid-row: 1;
		grid-column: 2 / -1;
		z-index: 1;
		align-self: start;
		justify-self: end;
		display: flex;
		flex-wrap: nowrap;
		gap: 6px;
		margin: 0;
		padding: 0;
		list-style: none;
	}

	.rail li {
		position: relative;
		display: flex;
		height: 38px;
		padding: 0 4px 6px;
		color: var(--muted-foreground);
		font-size: 13.5px;
		font-weight: 600;
		transition: color 0.35s var(--ease);
	}

	/* Each sector is a button that scrolls to it. */
	.rail button {
		display: flex;
		align-items: center;
		gap: 8px;
		margin: 0;
		padding: 0;
		border: 0;
		border-radius: 8px;
		background: none;
		color: inherit;
		font: inherit;
		cursor: pointer;
	}

	.rail button:focus-visible {
		outline: 2px solid var(--agent-rfq);
		outline-offset: 3px;
	}

	.rail li:not([data-active]) button:hover {
		color: var(--ink);
	}

	.rail li:not([data-active]) button:hover .r-icon {
		box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--ink) 40%, transparent);
		color: var(--ink);
		transform: translateY(-2px);
	}

	.rail li[data-active] {
		padding-right: 10px;
		color: var(--ink);
	}

	.rail li::before,
	.r-fill {
		content: "";
		position: absolute;
		right: 4px;
		bottom: 0;
		left: 4px;
		height: 2px;
		border-radius: 1px;
		background: var(--border);
	}

	.r-fill {
		background: var(--primary);
		transform: scaleX(0);
		transform-origin: left;
	}

	.rail li[data-done] .r-fill {
		transform: scaleX(1);
		opacity: 0.35;
	}

	.rail li[data-active] .r-fill {
		transform: scaleX(var(--u, 0));
	}

	.r-name {
		display: none;
	}

	/* The current sector's name has a fixed width, so the icons don't shift as it changes. */
	.rail li[data-active] .r-name {
		display: inline;
		min-width: 8.6em;
		white-space: nowrap;
	}

	.r-icon {
		display: grid;
		place-items: center;
		width: 26px;
		height: 26px;
		border-radius: 7px;
		box-shadow: inset 0 0 0 1px var(--border);
		background: #fff;
		transition:
			background-color 0.35s var(--ease),
			color 0.35s var(--ease),
			box-shadow 0.35s var(--ease),
			transform 0.5s var(--pop);
	}

	.rail li[data-active] .r-icon {
		background: var(--primary);
		box-shadow: 0 6px 14px -6px color-mix(in oklch, var(--primary) 70%, transparent);
		color: #fff;
		transform: scale(1.1);
	}

	/* Copy for each sector: the pain, the outcome, then how the agents do it. Lines arrive in turn. */
	.slide {
		grid-area: 1 / 1;
		display: flex;
		flex-direction: column;
		pointer-events: none;
	}

	/* Leaving, the copy goes a beat after the sheet (overlapping action). */
	.slide > :global(*) {
		opacity: 0;
		transform: translateY(12px);
		transition:
			opacity 0.25s var(--ease) 0.1s,
			transform 0.35s var(--ease) 0.1s;
	}

	.slide[data-active] {
		pointer-events: auto;
	}

	.slide[data-active] > :global(*) {
		opacity: 1;
		transform: none;
		transition:
			opacity 0.6s var(--ease),
			transform 0.7s var(--ease);
	}

	/* Left to right: the sector, its example, the pain, then the outcome and how. */
	.reg-name .slide[data-active] > :global(:nth-child(2)) { transition-delay: 0.14s; }
	.reg-pain .slide[data-active] > :global(*) { transition-delay: 0.24s; }
	.reg-out .slide[data-active] > :global(:nth-child(1)) { transition-delay: 0.34s; }
	.reg-out .slide[data-active] > :global(:nth-child(2)) { transition-delay: 0.44s; }

	/* The sector leads, set large. Its name rises out of a mask; the red index sits above it. */
	.sector {
		display: flex;
		flex-direction: column;
		gap: 6px;
		margin-bottom: 12px;
	}

	.slide > :global(.sector) {
		transform: none;
	}

	/* The red index arrives first and leads the eye in (anticipation); the name follows. */
	.s-no {
		color: var(--primary);
		font: 600 12px/1 var(--font-mono);
		letter-spacing: 0.08em;
		opacity: 0;
		transform: translateX(-10px);
		transition:
			opacity 0.2s var(--ease),
			transform 0.3s var(--ease);
	}

	.slide[data-active] .s-no {
		opacity: 1;
		transform: none;
		transition:
			opacity 0.4s var(--ease),
			transform 0.6s var(--pop);
	}

	/* The mask carries the name's size, so its padding (in em) clears the descenders ("y", "g")
	   without pushing the example line down. */
	.s-mask {
		display: block;
		overflow: hidden;
		margin-bottom: -0.08em;
		padding-bottom: 0.14em;
		font-size: clamp(3.5rem, min(7.5vw, 11svh), 6.75rem);
	}

	.s-name {
		display: block;
		color: var(--ink);
		font-family: "Inter Variable", var(--font-sans);
		font-variation-settings: "opsz" 32;
		font-weight: 650;
		line-height: 0.95;
		letter-spacing: -0.05em;
		transform: translateY(130%);
		transition: transform 0.45s var(--ease);
	}

	/* The name rises out of its mask, overshoots a touch and settles (slow out, follow-through). */
	.slide[data-active] .s-name {
		transform: none;
		transition: transform 0.85s cubic-bezier(0.34, 1.3, 0.64, 1) 0.12s;
	}

	/* The one concrete job this sector's sheet walks through. */
	.niche {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 4px 12px;
		color: var(--blue-800);
		font-size: 17px;
		font-weight: 500;
		line-height: 24px;
		text-wrap: pretty;
	}

	.niche span {
		color: var(--blue-700);
		font: 500 11px/16px var(--font-mono);
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.pain {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding-left: 16px;
		border-left: 2px solid var(--primary);
		color: var(--ink);
		font-size: 19px;
		font-weight: 500;
		line-height: 28px;
		letter-spacing: -0.01em;
		text-wrap: pretty;
	}

	.pain span {
		color: var(--primary);
		font: 500 11px/16px var(--font-mono);
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.how {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		margin-top: 22px;
		padding-top: 16px;
		border-top: 1px dashed var(--border);
		color: var(--blue-700);
		font-size: 14px;
		line-height: 21px;
	}

	.how b {
		color: var(--blue-800);
	}

	.avs {
		display: inline-flex;
		flex: none;
		padding-left: 5px;
	}

	.av {
		display: grid;
		place-items: center;
		width: 22px;
		height: 22px;
		margin-left: -5px;
		border-radius: 6px;
		background: var(--c);
		box-shadow: 0 0 0 2px #fff;
		color: #fff;
	}

	/* The stage is a drafting sheet: the crew at work on top, the drawing, then the title block. */
	.stage-wrap {
		position: relative;
	}

	/* A narrow sheet's title block drops the sector (it's set large right above), so the part fits. */
	@container (max-width: 520px) {
		.stage .tblock {
			grid-template-columns: minmax(0, 1fr) auto;
		}

		.stage .tblock span:first-child {
			display: none;
		}

		.stage .tblock span + span {
			border-left: 0;
		}

		.stage .tblock span:last-child {
			border-left: 1px solid var(--border);
		}
	}

	.stage {
		display: flex;
		flex-direction: column;
		container-type: inline-size;
		border: 1px solid color-mix(in oklch, var(--border) 80%, #fff);
		border-radius: 16px;
		background: linear-gradient(180deg, #fff, color-mix(in oklch, var(--surface) 70%, #fff));
		box-shadow:
			0 1px 0 #fff inset,
			0 1px 2px rgb(15 23 42 / 0.04),
			0 12px 24px -12px rgb(15 23 42 / 0.1),
			0 40px 80px -40px rgb(15 23 42 / 0.22);
	}

	.bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 12px 16px;
	}

	.crew {
		display: grid;
		min-width: 0;
	}

	.crew-row {
		grid-area: 1 / 1;
		display: flex;
		align-items: center;
		gap: 10px;
		min-width: 0;
		opacity: 0;
		transform: translateY(6px);
		transition:
			opacity 0.25s var(--ease),
			transform 0.35s var(--ease);
	}

	.crew-row[data-active] {
		opacity: 1;
		transform: none;
		transition:
			opacity 0.5s var(--ease) 0.15s,
			transform 0.6s var(--pop) 0.15s;
	}

	.crew-text {
		display: flex;
		flex-direction: column;
		min-width: 0;
		line-height: 1.25;
	}

	.crew-text b {
		overflow: hidden;
		color: var(--ink);
		font-size: 13px;
		font-weight: 600;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.state {
		display: grid;
		font-size: 12px;
	}

	.working,
	.ready {
		grid-area: 1 / 1;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		white-space: nowrap;
		transition:
			opacity 0.3s var(--ease),
			transform 0.45s var(--pop);
	}

	.working {
		color: var(--muted-foreground);
	}

	.ready {
		color: var(--ok);
		font-weight: 600;
		opacity: 0;
		transform: translateY(4px) scale(0.96);
	}

	.state[data-ready] .working {
		opacity: 0;
		transform: translateY(-4px);
	}

	.state[data-ready] .ready {
		opacity: 1;
		transform: none;
	}

	.dots {
		display: inline-flex;
		gap: 2px;
	}

	.dots i {
		width: 3.5px;
		height: 3.5px;
		border-radius: 50%;
		background: currentColor;
		animation: dot 1.1s var(--ease) infinite;
	}

	.dots i:nth-child(2) { animation-delay: 0.15s; }
	.dots i:nth-child(3) { animation-delay: 0.3s; }

	@keyframes dot {
		0%, 60%, 100% { opacity: 0.3; transform: none; }
		30% { opacity: 1; transform: translateY(-2px); }
	}

	.sheet-no {
		flex: none;
		color: var(--ink);
		font: 600 20px/1 var(--font-mono);
		letter-spacing: -0.02em;
	}

	.sheet-no small {
		margin-left: 2px;
		color: var(--muted-foreground);
		font-size: 12px;
		font-weight: 500;
	}

	/* The drawing area: fine grid paper with crop marks in the corners. */
	.view {
		position: relative;
		margin: 0 12px;
		container-type: inline-size;
		border: 1px solid var(--border);
		border-radius: 10px;
		background-color: #fff;
		background-image:
			linear-gradient(color-mix(in oklch, var(--blue-300) 14%, transparent) 1px, transparent 1px),
			linear-gradient(90deg, color-mix(in oklch, var(--blue-300) 14%, transparent) 1px, transparent 1px);
		background-size: 24px 24px;
		background-position: -1px -1px;
	}

	.view::before {
		--m: color-mix(in oklch, var(--ink) 45%, transparent);
		content: "";
		position: absolute;
		inset: 7px;
		background:
			linear-gradient(var(--m), var(--m)) top left / 10px 1px no-repeat,
			linear-gradient(var(--m), var(--m)) top left / 1px 10px no-repeat,
			linear-gradient(var(--m), var(--m)) top right / 10px 1px no-repeat,
			linear-gradient(var(--m), var(--m)) top right / 1px 10px no-repeat,
			linear-gradient(var(--m), var(--m)) bottom left / 10px 1px no-repeat,
			linear-gradient(var(--m), var(--m)) bottom left / 1px 10px no-repeat,
			linear-gradient(var(--m), var(--m)) bottom right / 10px 1px no-repeat,
			linear-gradient(var(--m), var(--m)) bottom right / 1px 10px no-repeat;
		pointer-events: none;
	}

	.box {
		position: relative;
		aspect-ratio: 520 / 330;
		overflow: hidden;
		border-radius: 10px;
		font-size: clamp(9.5px, 2.45cqw, 13px);
	}

	/* The title block, like the corner of a drawing sheet. */
	.tblock {
		display: grid;
		grid-template-columns: 1fr 1.4fr auto;
		margin: 12px 12px 12px;
		border: 1px solid var(--border);
		border-radius: 8px;
		background: #fff;
	}

	.tblock span {
		display: flex;
		flex-direction: column;
		gap: 2px;
		min-width: 0;
		padding: 7px 12px;
	}

	.tblock span + span {
		border-left: 1px solid var(--border);
	}

	.tblock small {
		color: var(--muted-foreground);
		font: 500 10px/14px var(--font-mono);
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.tblock b {
		overflow: hidden;
		color: var(--ink);
		font-size: 13px;
		font-weight: 600;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	/* The sheet's labels scale with the sheet, like the drawing under them, so a phone shows the same
	   sheet a laptop does: 13px on a full-size sheet, down to 7px on the narrowest phone. */
	.scene {
		position: absolute;
		inset: 0;
		font-size: clamp(7px, 2.45cqw, 13px);
		opacity: 0;
		transition: opacity 0.3s var(--ease);
	}

	/* Each sheet fades in while its drawing plots itself (see `.ink`). */
	/* Arriving, the sheet opens after the copy has cascaded in (LEAD_IN): name, example, pain, outcome,
	   then the drawing. Leaving, it goes first. */
	.scene {
		transition-duration: 0.2s;
	}

	.scene:global([data-active]) {
		opacity: 1;
		transition: opacity 0.45s var(--ease) 0.4s;
	}

	.scene > svg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		overflow: visible;
	}

	/* Anything switched on by the scroll pops in with a little overshoot and stays. */
	[data-s] {
		opacity: 0;
		transform: translateY(5px) scale(0.96);
		transition:
			opacity 0.3s var(--ease),
			transform 0.45s var(--pop);
	}

	[data-s]:global([data-on]) {
		opacity: 1;
		transform: none;
	}

	/* Drawn marks come in on a slight curve: the sideways drift and the rise use different easings,
	   so the path bends instead of running straight (arcs). */
	svg [data-s] {
		transform-box: fill-box;
		transform-origin: center;
		translate: -5px 0;
		transition:
			opacity 0.3s var(--ease),
			transform 0.45s var(--pop),
			translate 0.6s cubic-bezier(0.2, 0.75, 0.2, 1);
	}

	svg [data-s]:global([data-on]) {
		translate: 0 0;
	}

	/* Labels land tilted a hair and settle level as they arrive (follow-through and overlapping action). */
	.lab[data-s] {
		rotate: -2deg;
		transition:
			opacity 0.3s var(--ease),
			transform 0.45s var(--pop),
			rotate 0.6s var(--pop);
	}

	.lab[data-s]:global([data-on]) {
		rotate: 0deg;
	}

	/* Points and rings land with squash and stretch: flattened on impact, then a small rebound. */
	.anchor:global([data-on]),
	.ring:global([data-on]),
	.flag:global([data-on]),
	.sheet-ic.hit:global([data-on]) {
		animation: squash 0.5s var(--ease) both;
	}

	@keyframes squash {
		0% {
			transform: scale(0.3);
		}
		45% {
			transform: scale(1.25, 0.8);
		}
		70% {
			transform: scale(0.92, 1.08);
		}
		100% {
			transform: none;
		}
	}

	/* Calendar days fill from the bottom and stretch a little past full before settling. */
	.day[data-s] {
		translate: none;
		transform-origin: center bottom;
	}

	.day[data-s]:global([data-on]) {
		animation: rise 0.4s var(--ease) both;
	}

	@keyframes rise {
		0% {
			transform: scaleY(0);
		}
		65% {
			transform: scaleY(1.15);
		}
		100% {
			transform: none;
		}
	}

	/* Secondary action: when the sheet is done, the agent nods. */
	.stage:global([data-ready]) .crew-row[data-active] .avs {
		animation: nod 0.6s var(--ease);
	}

	@keyframes nod {
		0%,
		100% {
			transform: none;
		}
		30% {
			transform: translateY(-3px) rotate(-6deg);
		}
		60% {
			transform: translateY(1px) rotate(4deg);
		}
	}

	/* Staging: whatever is not in the current beat steps back, so the eye goes to the one thing that is. */
	.scene :global(g[data-k]) {
		transition: opacity 0.45s var(--ease);
	}

	.scene .lab[data-k] {
		transition:
			opacity 0.3s var(--ease),
			transform 0.45s var(--pop),
			rotate 0.6s var(--pop),
			filter 0.45s var(--ease);
	}

	.scene:global([data-beat="0"]) :global(g[data-k]:not([data-k~="0"])),
	.scene:global([data-beat="1"]) :global(g[data-k]:not([data-k~="1"])),
	.scene:global([data-beat="2"]) :global(g[data-k]:not([data-k~="2"])),
	.scene:global([data-beat="3"]) :global(g[data-k]:not([data-k~="3"])),
	.scene:global([data-beat="4"]) :global(g[data-k]:not([data-k~="4"])) {
		opacity: 0.2;
	}

	.scene:global([data-beat="0"]) .lab[data-k]:not([data-k~="0"]),
	.scene:global([data-beat="1"]) .lab[data-k]:not([data-k~="1"]),
	.scene:global([data-beat="2"]) .lab[data-k]:not([data-k~="2"]),
	.scene:global([data-beat="3"]) .lab[data-k]:not([data-k~="3"]),
	.scene:global([data-beat="4"]) .lab[data-k]:not([data-k~="4"]) {
		filter: opacity(0.25) saturate(0.3);
	}

	/*
	 * Drawing language: a drafting sheet in ink. Parts are hairline line work at true weights (outlines,
	 * thin dimension and extension lines, chain centerlines, hatching), with no fills. Everything an
	 * agent adds is in that agent's color (`--ag`) and uses the hero's pieces: attachment chips, mono
	 * labels and check marks.
	 */
	.box {
		--ink-line: oklch(0.32 0.02 262);
	}

	.defs {
		position: absolute;
	}

	.defs marker path {
		fill: var(--ink-line);
	}

	.defs pattern path {
		stroke: var(--ink-line);
		stroke-width: 0.6;
	}

	/* Outlines plot themselves, pen-plotter style, each time a sheet opens. */
	.ink {
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 1.5;
		stroke-linecap: round;
		stroke-linejoin: round;
		stroke-dasharray: 1;
		stroke-dashoffset: 0;
	}

	.scene:global([data-active]) .part .ink {
		animation: draw 1.1s cubic-bezier(0.45, 0, 0.2, 1) both;
		animation-delay: calc(0.4s + var(--d, 0) * 1s);
	}

	[data-s]:global([data-on]) .ink {
		animation: draw 1.1s cubic-bezier(0.45, 0, 0.2, 1) both;
		animation-delay: calc(var(--d, 0) * 1s);
	}

	@keyframes draw {
		from {
			stroke-dashoffset: 1;
		}
		to {
			stroke-dashoffset: 0;
		}
	}

	.thin {
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 0.75;
	}

	.ctr {
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 0.6;
		stroke-dasharray: 10 3 2 3;
		opacity: 0.5;
	}

	/* Dimensions and centerlines settle in once the outline is down. */
	.dim {
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 0.7;
		opacity: 0;
		transition: opacity 0.3s var(--ease);
	}

	.dim .arr {
		marker-start: url(#ss-arr);
		marker-end: url(#ss-arr);
	}

	.scene:global([data-active]) .dim {
		opacity: 0.8;
		transition: opacity 0.6s var(--ease) 0.9s;
	}

	.paper {
		fill: #fff;
		stroke: var(--border);
		stroke-width: 1;
	}

	.hatch {
		fill: url(#ss-hatch);
		opacity: 0.5;
	}

	/* What the agent touches: the feature itself, traced in its color. */
	.feat {
		fill: color-mix(in oklch, var(--ag) 8%, transparent);
		stroke: var(--ag);
		stroke-width: 2;
		stroke-linecap: round;
	}

	.feat.stock {
		fill: none;
		stroke-width: 1;
		stroke-dasharray: 12 3 2 3 2 3;
	}

	.lead[data-s] {
		translate: none;
		fill: none;
		stroke: var(--ag);
		stroke-width: 1.3;
		stroke-dasharray: 1;
		stroke-dashoffset: 1;
		opacity: 1;
		transform: none;
		transition: stroke-dashoffset 0.4s var(--ease);
	}

	/* The leader draws first; what it points to pops in at its end once it is there (data-u is set so). */
	.lead[data-s]:global([data-on]) {
		stroke-dashoffset: 0;
	}

	.anchor {
		fill: var(--ag);
		stroke: #fff;
		stroke-width: 2;
	}

	.ring {
		fill: color-mix(in oklch, var(--ag) 7%, transparent);
		stroke: var(--ag);
		stroke-width: 2;
	}

	.ring[data-s],
	.flag[data-s] {
		transform: scale(0.4);
	}

	.badge {
		fill: var(--ag);
	}

	/* Model-side marks follow the model's perspective: X-rayed holes, a traced rim, a matching dimension. */
	.xr-fill {
		fill: color-mix(in oklch, var(--ag) 16%, transparent);
	}

	.xr-hidden {
		fill: none;
		stroke: var(--ag);
		stroke-width: 1.1;
		stroke-dasharray: 3 2.5;
	}

	.xr-rim,
	.rim-hl {
		fill: none;
		stroke: var(--ag);
		stroke-width: 2;
	}

	.xr-halo {
		fill: color-mix(in oklch, var(--ag) 8%, transparent);
		stroke: color-mix(in oklch, var(--ag) 55%, transparent);
		stroke-width: 1.5;
	}

	.dim-ag {
		fill: none;
		stroke: var(--ag);
		stroke-width: 1.1;
	}

	.arrow-ag {
		fill: var(--ag);
	}

	.hl-box {
		fill: color-mix(in oklch, var(--ag) 10%, transparent);
		stroke: var(--ag);
		stroke-width: 2;
	}

	.flag text {
		fill: #fff;
		font: 700 10px var(--font-mono);
		text-anchor: middle;
	}

	/* Defense: drawings in memory, and the one that lifts out of the strip and opens. */
	.sheet-ic {
		fill: #fff;
		stroke: color-mix(in oklch, var(--ink-line) 45%, transparent);
		stroke-width: 0.8;
	}

	.sheet-ic.hit {
		fill: color-mix(in oklch, var(--ag) 12%, #fff);
		stroke: var(--ag);
		stroke-width: 1.2;
	}

	.beam {
		fill: color-mix(in oklch, var(--ag) 10%, transparent);
		stroke: color-mix(in oklch, var(--ag) 40%, transparent);
		transition: opacity 0.3s var(--ease);
	}

	.recall[data-s] {
		translate: none;
		transform-origin: 0 0;
		transform: translate(79px, 240px) scale(0.06);
		transition:
			opacity 0.25s var(--ease),
			transform 0.75s cubic-bezier(0.34, 1.2, 0.64, 1);
	}

	.recall[data-s]:global([data-on]) {
		transform: none;
	}

	.scan rect {
		fill: color-mix(in oklch, var(--ag) 10%, transparent);
	}

	.scan path {
		stroke: var(--ag);
		stroke-width: 1.5;
	}

	.scan {
		transition: opacity 0.3s var(--ease);
	}

	/* Automotive calendars: hatched days for the shop without Pre6, solid agent color with it. */
	.day {
		fill: color-mix(in oklch, var(--ink-line) 7%, #fff);
		stroke: color-mix(in oklch, var(--ink-line) 18%, transparent);
		stroke-width: 0.6;
	}

	.day.slow {
		fill: url(#ss-hatch);
		stroke: var(--ink-line);
	}

	.day.fast {
		fill: var(--ag);
		stroke: var(--ag);
	}

	.day.next {
		fill: color-mix(in oklch, var(--ag) 14%, #fff);
		stroke: color-mix(in oklch, var(--ag) 30%, transparent);
	}

	/* Labels: HTML placed in the drawing's coordinates, centered on their y. */
	.lab {
		position: absolute;
		translate: 0 -50%;
		white-space: nowrap;
	}

	/* Icons in the labels scale with the text, like everything else on the sheet. */
	.lab :global(svg.lucide-icon) {
		flex: none;
		width: 0.92em;
		height: 0.92em;
	}

	.note,
	.year,
	.who {
		color: var(--muted-foreground);
		font: 500 1em/1 var(--font-mono);
		letter-spacing: 0.04em;
	}

	.year {
		translate: -50% -50%;
	}

	.who.strong {
		color: var(--ink);
		font-weight: 600;
	}

	.dimtxt {
		translate: -50% -50%;
		padding: 0 3px;
		background: #fff;
		color: var(--ink-line);
		font: 500 1em/1 var(--font-mono);
	}

	/* The hero's attachment chip, in the acting agent's color. */
	.att {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		max-width: 92%;
		padding: 0.38em 0.62em;
		border-radius: 7px;
		background: color-mix(in oklch, var(--ag) 6%, #fff);
		box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--ag) 30%, transparent);
		color: var(--ag);
		font-size: 1em;
	}

	.att b {
		font: 600 1em var(--font-mono);
	}

	.att span {
		overflow: hidden;
		color: var(--muted-foreground);
		text-overflow: ellipsis;
	}

	/* A cost line: the attachment chip with its value and source. */
	.cost {
		display: grid;
		grid-template-columns: 1fr auto;
		gap: 2px 8px;
		padding: 0.42em 0.65em;
		border-radius: 7px;
		background: #fff;
		box-shadow:
			inset 0 0 0 1px color-mix(in oklch, var(--ag) 30%, transparent),
			0 6px 14px -10px color-mix(in oklch, var(--ag) 60%, transparent);
	}

	.c-k {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		color: var(--ink);
		font-weight: 600;
	}

	.c-k :global(svg) {
		color: var(--ag);
	}

	.cost > b {
		color: var(--ag);
		font: 600 1em var(--font-mono);
	}

	.c-src {
		grid-column: 1 / -1;
		overflow: hidden;
		color: var(--muted-foreground);
		font: 500 1em var(--font-mono);
		text-overflow: ellipsis;
	}

	/* The first-article stamp, set at a slight angle like an inspector's stamp. */
	.stamp[data-s] {
		padding: 0.3em 0.7em;
		border: 1.5px solid var(--ok);
		border-radius: 4px;
		color: var(--ok);
		font: 700 1em/1 var(--font-mono);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		transform: rotate(-4deg) scale(1.6);
	}

	/* The stamp slams down: it squashes on impact and springs back (exaggeration, squash and stretch). */
	.stamp[data-s]:global([data-on]) {
		transform: rotate(-4deg);
		animation: slam 0.55s var(--ease) both;
	}

	@keyframes slam {
		0% {
			opacity: 0;
			transform: rotate(-4deg) scale(1.6);
		}
		50% {
			opacity: 1;
			transform: rotate(-4deg) scale(0.94, 0.84);
		}
		75% {
			transform: rotate(-4deg) scale(1.04, 1.04);
		}
		100% {
			transform: rotate(-4deg);
		}
	}

	/* The recalled drawing's title block. */
	.tb {
		translate: -50% -50%;
		color: var(--ink-line);
		font: 600 1em/1 var(--font-mono);
	}

	/* The spares RFQ, like a message in the hero's inbox, collecting what the agent found. */
	.rfq {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 5px;
		translate: 0 0;
		padding: 0.6em 0.7em;
		border-radius: 9px;
		background: #fff;
		box-shadow:
			inset 0 0 0 1px var(--border),
			0 10px 24px -16px rgb(15 23 42 / 0.25);
		white-space: normal;
	}

	.r-t {
		color: var(--ink);
		font-size: 1em;
		font-weight: 600;
	}

	.rfq .att {
		max-width: 100%;
		white-space: nowrap;
	}

	/* A chip with a long note wraps under its label instead of cutting the note off. */
	.rfq .att.wrap {
		align-items: flex-start;
		white-space: normal;
	}

	.att.wrap span {
		text-overflow: clip;
	}

	/* "Searching memory…" and the result share one cell, so the longer one sets the height and
	   either can wrap in a narrow card. */
	.status {
		display: grid;
		color: var(--muted-foreground);
		font-size: 1em;
	}

	.status > :global(*) {
		grid-area: 1 / 1;
	}

	.done {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		background: #fff;
		color: var(--ok);
		font-weight: 600;
	}

	.sent {
		translate: -100% -50%;
		display: inline-flex;
		align-items: center;
		gap: 4px;
		color: var(--muted-foreground);
		font: 600 1em/1 var(--font-mono);
	}

	.sent.ok {
		color: var(--ok);
	}

	/* The bill of materials: a drawing's parts list, each row costed by the agent in turn. */
	.bom {
		translate: 0 0;
		overflow: hidden;
		border-radius: 8px;
		background: #fff;
		box-shadow: inset 0 0 0 1px var(--border);
		font-size: 1em;
	}

	.bom-h,
	.bom-r,
	.bom-more {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 0.42em 0.7em;
	}

	.bom-h {
		justify-content: space-between;
		background: color-mix(in oklch, var(--ink-line) 4%, #fff);
		color: var(--muted-foreground);
		font: 500 1em/1.2 var(--font-mono);
	}

	.bom-h b {
		color: var(--ag);
	}

	.bom-r {
		border-top: 1px solid color-mix(in oklch, var(--border) 70%, transparent);
	}

	.bom-r i {
		display: grid;
		flex: none;
		place-items: center;
		width: 1.5em;
		height: 1.5em;
		border-radius: 50%;
		box-shadow: inset 0 0 0 1px var(--ink-line);
		color: var(--ink-line);
		font: 600 1em/1 var(--font-mono);
		font-style: normal;
	}

	.bom-r span {
		flex: 1;
		color: var(--ink);
	}

	.bom-r b {
		color: var(--ag);
		font: 600 1em var(--font-mono);
	}

	.bom-more {
		border-top: 1px solid color-mix(in oklch, var(--border) 70%, transparent);
		color: var(--muted-foreground);
		font: 500 1em var(--font-mono);
	}

	/* What changed in a revision is drawn in the agent's color; everything carried over stays in ink. */
	.ink.new {
		stroke: var(--ag);
	}

	.arrow-r {
		stroke-width: 1;
		marker-end: url(#ss-arr);
	}

	.rev {
		color: var(--ink);
		font: 600 1em var(--font-mono);
	}

	.err path {
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 1.2;
	}

	.err circle {
		fill: var(--ag);
		stroke: #fff;
		stroke-width: 2;
	}

	.actual[data-s] {
		translate: none;
		fill: none;
		stroke: var(--ink-line);
		stroke-width: 1;
		stroke-dasharray: 5 4;
		transform: none;
	}

	.pct {
		padding: 0 3px;
		background: #fff;
		color: var(--ag);
		font: 600 1em var(--font-mono);
	}

	/* Mid-size screens: the sheet on the left, the pain and the outcome stacked on its right. */
	@media (min-width: 1024px) and (max-width: 1279px) {
		.frame {
			grid-template-columns: minmax(0, 1fr) minmax(0, 19rem);
			grid-template-rows: auto auto auto;
			grid-template-areas:
				"name name"
				"stage pain"
				"stage out";
			row-gap: 20px;
		}

		.rail {
			grid-column: 1 / -1;
		}

		.reg-pain {
			align-self: end;
		}

		.reg-out {
			align-self: start;
		}

		.stage-wrap {
			max-width: min(620px, calc((100svh - var(--nav-h) - 280px) * 1.35));
		}

		.reg-pain::before,
		.reg-pain::after,
		.reg-out::before,
		.reg-out::after {
			display: none;
		}
	}

	@media (max-width: 1023px) {
		.sectors {
			height: calc(100svh + var(--steps) * 65svh);
		}

		/* Phones and tablets stack: sectors, name, pain, outcome, then the sheet. */
		.frame {
			grid-template-columns: minmax(0, 1fr);
			grid-template-rows: none;
			grid-template-areas:
				"rail"
				"name"
				"pain"
				"out"
				"stage";
			/* The copy takes what it needs from the top, so the sector icons never slip under the navbar,
			   and the sheet gets the rest. */
			grid-template-rows: auto auto auto auto minmax(0, 1fr);
			align-items: start;
			gap: 12px;
			padding-block: 12px;
		}

		.rail {
			grid-area: rail;
			justify-self: start;
		}

		/* The sheet takes the height the copy leaves, so the whole frame fits on one screen however the
		   copy wraps: its width comes from that height (the drawing is 520 × 330, plus the sheet's bar,
		   title block and margins, --chrome tall and --side wide). */
		.stage-wrap {
			--chrome: 136px;
			--side: 28px;
			align-self: stretch;
			max-width: none;
			min-height: 0;
			margin-top: 6px;
			container-type: size;
		}

		.stage-wrap > .stage {
			width: min(100%, max(160px, calc((100cqh - var(--chrome)) * 520 / 330 + var(--side))));
			margin-inline: auto;
		}

		.reg-pain::before,
		.reg-pain::after,
		.reg-out::before,
		.reg-out::after {
			display: none;
		}

		.s-mask {
			font-size: clamp(2.6rem, 8vw, 4rem);
		}

		.pain {
			font-size: 16px;
			line-height: 24px;
		}

		.how {
			margin-top: 14px;
			padding-top: 12px;
		}
	}

	@media (max-width: 639px) {
		.sectors {
			--gutter: 16px;
		}

		.rail li {
			font-size: 13px;
		}

		.sector {
			margin-bottom: 6px;
		}

		.s-mask {
			font-size: 2.4rem;
		}

		.pain {
			font-size: 14.5px;
			line-height: 21px;
		}

		.niche {
			margin-bottom: 12px;
			font-size: 14.5px;
			line-height: 20px;
		}

		/* Phones keep the sector, the example, the pain, the outcome and the sheet. The crew on the sheet
		   says who did it. */
		.slide :global(.body),
		.how,
		.tblock span:last-child {
			display: none;
		}

		.tblock {
			grid-template-columns: 1fr 1.5fr;
		}

		.stage-wrap {
			--chrome: 121px;
			--side: 20px;
		}

		.bar {
			padding: 10px 12px;
		}

		/* A long "working" line wraps under the agent's name rather than running into the sheet number. */
		.working,
		.ready {
			white-space: normal;
		}

		.view {
			margin: 0 8px;
		}

		.tblock {
			margin: 8px;
		}
	}

	/* Short laptop screens (a 768px screen under browser toolbars): the title block goes first, then
	   the agent bar, so the sheet keeps a readable size. */
	@media (min-width: 1024px) and (max-height: 760px) {
		.tblock {
			display: none;
		}

		.view {
			margin-bottom: 12px;
		}

		.stage-wrap {
			max-width: min(660px, calc((100svh - var(--nav-h) - 270px) * 1.5));
		}
	}

	/* Short laptop screens also drop the "How" line, and the narrower ones set the outcome smaller, so
	   the right column fits beside the sheet. */
	@media (min-width: 1024px) and (max-height: 760px) {
		.how {
			display: none;
		}
	}

	@media (min-width: 1024px) and (max-width: 1279px) and (max-height: 820px) {
		.reg-out :global(h2) {
			font-size: 1.6rem;
			line-height: 1.15;
		}
	}

	@media (min-width: 1024px) and (max-height: 640px) {
		.bar {
			display: none;
		}

		.view {
			margin-top: 12px;
		}

		.stage-wrap {
			max-width: min(660px, calc((100svh - var(--nav-h) - 230px) * 1.55));
		}
	}

	/* Short screens: the title block repeats the sector and part named above it, so it goes first. */
	@media (max-width: 1023px) and (max-height: 760px) {
		.tblock {
			display: none;
		}

		.view {
			margin-bottom: 12px;
		}

		.stage-wrap {
			--chrome: 74px;
		}
	}

	@media (max-width: 639px) and (max-height: 760px) {
		.view {
			margin-bottom: 8px;
		}

		.stage-wrap {
			--chrome: 64px;
		}
	}

	/* Very short screens (an iPhone SE): the sheet's agent bar and the example line go too, so the
	   sheet stays big enough to read. */
	@media (max-width: 1023px) and (max-height: 700px) {
		.bar,
		.niche {
			display: none;
		}

		.reg-out :global(h2) {
			font-size: 1.5rem;
			line-height: 1.15;
		}

		.view {
			margin-top: 8px;
		}

		.stage-wrap {
			--chrome: 26px;
		}
	}

	/* Phones on their side: the copy on the left, the sheet on the right. */
	@media (max-width: 1023px) and (max-height: 500px) and (orientation: landscape) {
		.frame {
			grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
			grid-template-rows: auto auto auto minmax(0, 1fr);
			grid-template-areas:
				"rail rail"
				"name stage"
				"pain stage"
				"out stage";
			gap: 8px 24px;
			padding-block: 8px;
		}

		.stage-wrap {
			grid-row: 2 / 5;
			margin-top: 0;
		}

		.s-mask {
			font-size: 2.2rem;
		}

		.sector {
			margin-bottom: 0;
		}

		.slide :global(.body),
		.how {
			display: none;
		}
	}

	@media (max-width: 1023px) and (max-height: 420px) and (orientation: landscape) {
		.pain > span,
		.out-k {
			display: none;
		}

		.outcome {
			gap: 0;
		}
	}

	/* The shortest landscape phones drop the sector icons, the one thing left that fits elsewhere
	   (the sector's name is right there). */
	@media (max-width: 1023px) and (max-height: 340px) and (orientation: landscape) {
		.frame {
			grid-template-rows: auto auto minmax(0, 1fr);
			grid-template-areas:
				"name stage"
				"pain stage"
				"out stage";
		}

		.rail {
			display: none;
		}

		.stage-wrap {
			grid-row: 1 / 4;
		}
	}

	/* The narrowest phones tighten the sector labels so all five fit across. */
	@media (max-width: 359px) {
		.rail li {
			font-size: 11px;
		}
	}

	/* The shortest portrait phones (an original iPhone SE) drop the small "pain" and "outcome" labels;
	   the red and green rules still mark them. */
	@media (max-width: 639px) and (max-height: 600px) {
		.pain > span,
		.out-k {
			display: none;
		}

		.outcome {
			gap: 0;
		}

		.frame {
			gap: 10px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.slide > :global(*),
		.scene,
		[data-s],
		.lead[data-s] {
			transition: none;
		}

		.scene:global([data-active]),
		.dots i,
		[data-s]:global([data-on]),
		.stage:global([data-ready]) .crew-row[data-active] .avs {
			animation: none;
		}

		svg [data-s] {
			translate: none;
		}

		.lab[data-s] {
			rotate: none;
		}

		.ink {
			animation: none !important;
		}
	}
</style>
